/**
 * @fileoverview Edit-session state for the Capabilities page.
 * @description Owns the matrix query and everything about editing it: the session (null when not
 * editing), the list of changes it implies, starting, cancelling and toggling, saving (with the
 * confirmation message and the mutation's error). Starting an edit freezes the matrix the admin
 * sees — a refetch while editing (a failed save's, a manual retry) never moves the highlights, the
 * change count or the row versions under them — and pauses the automatic refetches, so a Save
 * always sends exactly what was on screen, named by the row versions it was loaded at. A save
 * refused as stale (412) re-bases only the roles someone else changed: their edits are discarded
 * and replaced with the live grants, while every other role keeps its edits. A save too large for
 * one request is flagged before it is sent. Keeps the page itself a thin composition of the pieces.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CAPABILITIES_QUERY_KEYS } from '@/features/capabilities/constants/capabilities-query-keys.constants'
import { useCapabilityMatrix } from '@/features/capabilities/hooks/useCapabilityMatrix'
import { useSaveCapabilityChanges } from '@/features/capabilities/hooks/useSaveCapabilityChanges'
import {
  buildDraft,
  buildLimitWarning,
  collectChanges,
  findPermission,
  rebaseStaleRoles,
  toggleDraft,
} from '@/features/capabilities/utils/capability-draft.utils'

import type {
  CapabilityDraft,
  CapabilityMatrix,
} from '@/features/capabilities/types/capabilities.types'

const STALE_VERSION_STATUS = 412

/**
 * One edit session: the matrix as it was when editing started (or as last re-based), the draft over
 * it, and the roles a refused save just re-based, for the message about it.
 */
interface EditSession {
  base: CapabilityMatrix
  draft: CapabilityDraft
  conflictRoleNames: string[]
}

/**
 * @description Holds the edit session: starting one from a matrix, toggling a cell, ending it, and
 * re-basing the stale roles after a refused save.
 * @returns {object} What the session shows (the frozen matrix, the draft, the changes it implies and
 * the names of the roles just re-based; empty when not editing) and the handlers that change it.
 */
function useEditSession() {
  const queryClient = useQueryClient()
  const [session, setSession] = useState<EditSession | null>(null)

  return {
    editing: session !== null,
    base: session?.base,
    draft: session?.draft ?? null,
    changes: session ? collectChanges(session.base, session.draft) : [],
    conflictRoles: session?.conflictRoleNames.join(', ') ?? '',
    /**
     * @description Starts a session on the matrix as shown now, with nothing changed yet.
     * @param {CapabilityMatrix} matrix - The matrix to freeze and copy into a draft.
     */
    begin: (matrix: CapabilityMatrix) =>
      setSession({ base: matrix, draft: buildDraft(matrix), conflictRoleNames: [] }),
    /**
     * @description Ends the session, discarding any draft.
     */
    end: () => setSession(null),
    /**
     * @description Flips one cell in the draft.
     * @param {string} roleCode - The role whose cell was clicked.
     * @param {string} permissionCode - The permission whose cell was clicked.
     */
    toggle: (roleCode: string, permissionCode: string) =>
      setSession((current) => {
        const permission = current ? findPermission(current.base, permissionCode) : undefined
        if (!current || !permission) {
          return current
        }

        return { ...current, draft: toggleDraft(current.draft, permission, roleCode) }
      }),
    /**
     * @description Re-bases the roles a refused save found stale onto the matrix as refetched after
     * the refusal (the mutation settles only once that refetch has), keeping every other role's
     * edits.
     */
    rebaseAfterConflict: () => {
      const live = queryClient.getQueryData<CapabilityMatrix>(CAPABILITIES_QUERY_KEYS.matrix)
      if (!live) {
        return
      }

      setSession((current) => {
        if (!current) {
          return current
        }

        const { base, draft, staleRoleNames } = rebaseStaleRoles(current.base, current.draft, live)
        return { base, draft, conflictRoleNames: staleRoleNames }
      })
    },
  }
}

/**
 * @description Runs the Capabilities matrix query and its edit session.
 * @returns {object} The matrix to show (frozen while editing) and its query, the draft and its
 * changes, the save state and error, the flash message, the warning when a save is too large, and
 * the handlers that drive them.
 */
export function useCapabilitiesEditor() {
  const { t } = useTranslation()
  const edit = useEditSession()
  const { changes } = edit
  const matrixQuery = useCapabilityMatrix(edit.editing)
  const saveMutation = useSaveCapabilityChanges()
  const [flash, setFlash] = useState('')

  const matrix = edit.base ?? matrixQuery.data
  const limitWarning = buildLimitWarning(changes, matrix)
  const isStale = saveMutation.isError && saveMutation.error.status === STALE_VERSION_STATUS

  let saveError = ''
  if (saveMutation.isError) {
    if (!isStale) {
      saveError = saveMutation.error.message
    } else {
      saveError = edit.conflictRoles
        ? t('capabilities.saveConflict', { roles: edit.conflictRoles })
        : t('capabilities.saveConflictUnknown')
    }
  }

  return {
    matrix,
    matrixQuery,
    draft: edit.draft,
    editing: edit.editing,
    changes,
    flash,
    dismissFlash: () => setFlash(''),
    saveError,
    limitWarning,
    isSaving: saveMutation.isPending,
    toggle: edit.toggle,
    /**
     * @description Starts editing the matrix as shown now.
     */
    startEditing: () => {
      if (matrixQuery.data) {
        saveMutation.reset()
        setFlash('')
        edit.begin(matrixQuery.data)
      }
    },
    /**
     * @description Abandons the edits and goes back to the read-only table.
     */
    cancelEditing: () => {
      saveMutation.reset()
      edit.end()
    },
    /**
     * @description Saves the changed cells, then leaves edit mode with a confirmation message. A
     * save refused as stale stays in edit mode with only the stale roles re-based and its error
     * showing, so the admin reviews and saves again.
     */
    save: () => {
      if (limitWarning) {
        return
      }

      saveMutation.mutate(changes, {
        /**
         * @description Leaves edit mode and confirms how many changes took effect.
         * @param {{ saved: number }} response - The save's result.
         */
        onSuccess: (response) => {
          edit.end()
          setFlash(
            response.saved > 0
              ? t('capabilities.flash.saved', { count: response.saved })
              : t('capabilities.flash.nothingChanged'),
          )
        },
        /**
         * @description Re-bases the stale roles when the save was refused as stale.
         * @param {{ status?: number }} error - The save's error.
         */
        onError: (error) => {
          if (error.status === STALE_VERSION_STATUS) {
            edit.rebaseAfterConflict()
          }
        },
      })
    },
  }
}
